// src/screens/Marketplace/MarketSearch.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./MarketSearch.css";
import { useMarketContext } from "../../context/MarketContext";
import { useActiveStores } from "../../context/ActiveStoresContext";
import { basePi, saleViewFor } from "../../utils/pricing";
import api from "../../api";

import backIcon from "../../assets/backspace-icon.png";
import clearIcon from "../../assets/reload-icon.png";

// ---------------------------
// Intent dictionaries
// ---------------------------

// Category synonyms → one of: "barter" | "physical" | "digital" | "service"
const CATEGORY_SYNONYMS = [
  { words: ["service", "services", "gig", "freelance", "hire", "repair"], value: "service" },
  { words: ["digital", "ebook", "e-book", "file", "download", "license", "code"], value: "digital" },
  { words: ["barter", "swap", "trade"], value: "barter" },
  { words: ["product", "goods", "item", "physical"], value: "physical" },
];

// Department keywords → backend labels (to match what AddProduct sends)
const DEPARTMENT_KEYWORDS = [
  { words: ["jewelry", "jewellery", "watch", "watches", "necklace", "ring", "bracelet"], value: "Jewelry & Watches" },
  { words: ["electronics", "phone", "smartphone", "gadget", "laptop", "pc", "camera"], value: "Electronics & Gadgets" },
  { words: ["clothes", "clothing", "fashion", "shirt", "shoe", "sneaker", "bag"], value: "Clothing & Accessories" },
  { words: ["toy", "toys", "game", "boardgame"], value: "Toys & Games" },
  { words: ["home", "furniture", "sofa", "kitchen", "appliance"], value: "Home & Living" },
  { words: ["book", "books", "stationery", "notebook", "pen"], value: "Books & Stationery" },
  { words: ["art", "painting", "print", "poster", "collectible", "collectibles"], value: "Art & Collectibles" },
  { words: ["health", "essential", "essentials", "sanitizer", "mask", "vitamin"], value: "Health & Essentials" },
  { words: ["sport", "sports", "ball", "gym", "outdoor", "camp"], value: "Sports & Outdoors" },
  { words: ["luggage", "travel", "suitcase", "bag"], value: "Luggage & Travel" },
  { words: ["course", "courses", "tutorial", "ebook", "e-book"], value: "Courses & E-Books" },
  { words: ["gaming", "console", "ps4", "ps5", "xbox", "switch"], value: "Gaming Items" },
  { words: ["voucher", "vouchers", "subscription", "data", "airtime"], value: "Subscriptions & Vouchers" },
  { words: ["nft", "nfts"], value: "NFTs (Soon)" },
];

// Delivery keywords → normalized tokens we’ll match against product delivery
const DELIVERY_KEYWORDS = [
  { words: ["pickup", "pick-up", "collection", "collect"], value: "Local Pickup" },
  { words: ["meet", "meetup", "meet-up"], value: "Meet-up (in person)" },
  { words: ["electronic", "email", "download", "instant"], value: "Electronic Delivery" },
  { words: ["paxi", "pep"], value: "PEP (PAXI)" },
  { words: ["postnet"], value: "PostNet" },
  { words: ["aramex"], value: "Aramex" },
  { words: ["courier"], value: "The Courier Guy" }, // generic “courier” → a common one
  { words: ["dhl"], value: "DHL" },
  { words: ["fedex"], value: "FedEx" },
  { words: ["ups"], value: "UPS" },
  { words: ["fastway"], value: "Fastway" },
];

// Condition keywords
const CONDITION_KEYWORDS = [
  { words: ["new", "brand-new", "sealed"], value: "new" },
  { words: ["used", "preloved", "secondhand", "second-hand", "pre-owned", "preowned"], value: "used" },
];




// Utility to debounce an input value
function useDebouncedValue(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

// ---------------------------
// Query parsing
// ---------------------------
function normalize(str = "") {
  return (str || "").toString().toLowerCase();
}

// Pulls out: category, department, delivery, condition, maxDays, and leftover terms
function parseQuery(raw) {
  const orig = raw || "";
  let q = normalize(orig);

  // Extract delivery days like: "3 days", "within 7 days", "7day", "7days"
  let maxDays = null;
  const dayMatch = q.match(/(?:within\s*)?(\d{1,3})\s*(day|days)\b/);
  if (dayMatch) {
    maxDays = parseInt(dayMatch[1], 10);
    q = q.replace(dayMatch[0], " ").trim();
  }

  // Helper: consume best match from a dictionary → returns {value, matchedWord}
  function pull(dict) {
    for (const row of dict) {
      for (const w of row.words) {
        const re = new RegExp(`\\b${w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
        const m = q.match(re);
        if (m) {
          q = q.replace(re, " ").trim();
          return { value: row.value, matched: m[0] };
        }
      }
    }
    return { value: null, matched: null };
  }

  const { value: category, matched: catWord } = pull(CATEGORY_SYNONYMS);
  const { value: department, matched: depWord } = pull(DEPARTMENT_KEYWORDS);
  const { value: delivery, matched: delWord } = pull(DELIVERY_KEYWORDS);
  const { value: condition, matched: condWord } = pull(CONDITION_KEYWORDS);

  // remaining terms = free text
  const terms = q
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean);

  // Record which words we consumed, to power chips that can be removed
  const consumed = [catWord, depWord, delWord, condWord].filter(Boolean);

  return { terms, category, department, delivery, condition, maxDays, consumed };
}

// ---------------------------
// Scoring
// ---------------------------
function arrify(x) {
  if (!x) return [];
  if (Array.isArray(x)) return x;
  // some products send single string or JSON string for delivery
  if (typeof x === "string") {
    try {
      const parsed = JSON.parse(x);
      if (Array.isArray(parsed)) return parsed;
    } catch {}
    return [x];
  }
  return [x];
}

// Highlight helper (like Search.js)
function highlightMatch(text, query) {
  if (!text || !query) return text || '';
  const safe = String(query).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // escape regex
  const re = new RegExp(`(${safe})`, 'gi');
  return String(text).replace(re, '<mark>$1</mark>');
}



function textScore(haystack, needles) {
  const h = normalize(haystack);
  if (!h || needles.length === 0) return 0;
  let s = 0;
  for (const n of needles) {
    const needle = normalize(n);
    if (!needle) continue;
    if (h === needle) s += 3; // exact field match
    if (h.startsWith(needle)) s += 2;
    if (h.includes(needle)) s += 1;
  }
  return s;
}

function productMatchesHardFilters(p, filters) {
  // Hard constraints: maxDays (<=), optional department/category/condition/delivery
  if (Number.isInteger(filters.maxDays)) {
    const d = Number(p.expectedDeliveryDays);
    if (!(Number.isFinite(d) && d <= filters.maxDays)) return false;
  }
  return true;
}

function scoreProduct(p, filters) {
  const {
    terms = [],
    category,
    department,
    condition,
    delivery,
  } = filters;

  const name = p.name || "";
  const desc = p.description || "";
  const store = p.owner?.businessName || "";
  const username = p.owner?.businessUsername || "";

  // Base relevance from text fields
  let score =
    textScore(name, terms) * 3 +
    textScore(desc, terms) * 1 +
    textScore(store, terms) * 2 +
    textScore(username, terms) * 1;

  // Soft boosts for inferred filters
  if (category && normalize(p.category) === category) score += 8;
  if (department && normalize(p.department) === normalize(department)) score += 8;
  if (condition && normalize(p.condition) === condition) score += 5;

  if (delivery) {
    const methods = arrify(p.deliveryMethod);
    if (methods.map(normalize).includes(normalize(delivery))) score += 6;
  }

  // Slight boost if item is on sale (tends to be attractive in search)
  if (p.sale) score += 1;

  // Tiny boost for recent-ish listings if available
  if (p.createdAt) {
    try {
      const ageMs = Date.now() - new Date(p.createdAt).getTime();
      const days = Math.max(0, ageMs / (1000 * 60 * 60 * 24));
      // newer → higher score (cap the effect)
      score += Math.max(0, 3 - Math.min(3, days / 7));
    } catch {}
  }

  return score;
}

// --------------------------------------------------

export default function MarketSearch({ user }) {
  const navigate = useNavigate();
  const { products: listings, loadListings } = useMarketContext();
  const { activeStores } = useActiveStores();

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);
  const [tab, setTab] = useState("Products"); // "Products" | "Stores"
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);

  // ✅ Pi→USD rate for dynamic pricing + sale math
  const [piToUsdRate, setPiToUsdRate] = useState(null);
  useEffect(() => {
    const fetchPiRate = async () => {
      try {
        const res = await api.get("/convert/rate");
        if (res.data?.piToUsd) setPiToUsdRate(res.data.piToUsd);
      } catch (err) {
        console.error("❌ Failed to fetch cached Pi rate:", err?.message);
      }
    };
    fetchPiRate();
    const id = setInterval(fetchPiRate, 600000); // 10 min
    return () => clearInterval(id);
  }, []);

  // Load initial data if user hard-navigates directly here
  useEffect(() => {
    if (listings.length === 0) {
      loadListings(true, user?.username);
    }
  }, [listings.length, loadListings, user?.username]);

  // Recent searches in localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem("bf_recent_market_searches");
      if (raw) setRecent(JSON.parse(raw).slice(0, 8));
    } catch {}
  }, []);

  const saveRecent = (q) => {
    if (!q?.trim()) return;
    try {
      const raw = localStorage.getItem("bf_recent_market_searches");
      const arr = raw ? JSON.parse(raw) : [];
      const next = [q, ...arr.filter((x) => x.toLowerCase() !== q.toLowerCase())].slice(0, 8);
      localStorage.setItem("bf_recent_market_searches", JSON.stringify(next));
      setRecent(next);
    } catch {}
  };

  const clearRecent = () => {
    localStorage.removeItem("bf_recent_market_searches");
    setRecent([]);
  };

  // ------------- New: smart parsing -------------
  const q = debouncedQuery.trim();
  const parsed = useMemo(() => parseQuery(q), [q]);

  // Products: rank by score + apply hard filters (like maxDays)
  const productResults = useMemo(() => {
    if (!q) return [];
    const filtered = listings.filter((p) => productMatchesHardFilters(p, parsed));

    // If user typed pure intent (e.g., just "services"), still allow matches with no terms
    const scored = filtered.map((p) => ({ p, score: scoreProduct(p, parsed) }));

    // If literally no score (e.g. super empty data), fallback to contains check on name/store/user
    const anyPositive = scored.some((x) => x.score > 0);
    let finalArr = scored;
    if (!anyPositive) {
      const nq = q.toLowerCase();
      finalArr = filtered
        .filter((p) => {
          const name = (p.name || "").toLowerCase();
          const store = (p.owner?.businessName || "").toLowerCase();
          const username = (p.owner?.businessUsername || "").toLowerCase();
          return name.includes(nq) || store.includes(nq) || username.includes(nq);
        })
        .map((p) => ({ p, score: 0 }));
    }

    // Sort high → low score
    finalArr.sort((a, b) => b.score - a.score);
    return finalArr.map((x) => x.p);
  }, [q, listings, parsed]);

  // Stores: keep simple text match
  const storeResults = useMemo(() => {
    if (!q) return [];
    const nq = q.toLowerCase();
    return (activeStores || []).filter((s) => {
      const n = (s.businessName || "").toLowerCase();
      const u = (s.businessUsername || "").toLowerCase();
      return n.includes(nq) || u.includes(nq);
    });
  }, [q, activeStores]);

// Quick suggestions (non-strict): top names that contain the typed text
const quickSuggestions = useMemo(() => {
  if (!q) return [];
  const nq = q.toLowerCase();

  // Products: take up to 6 distinct name matches
  const prodMatches = [];
  const seenProd = new Set();
  for (const p of listings) {
    const name = p.name || '';
    if (name && name.toLowerCase().includes(nq) && !seenProd.has(name)) {
      prodMatches.push({ type: 'product', id: p._id, label: name, product: p });
      seenProd.add(name);
      if (prodMatches.length >= 6) break;
    }
  }

  // Stores: take up to 6 distinct store name/handle matches
  const storeMatches = [];
  const seenStore = new Set();
  for (const s of (activeStores || [])) {
    const name = s.businessName || '';
    const handle = s.businessUsername || '';
    const storeHit = (name && name.toLowerCase().includes(nq)) || (handle && handle.toLowerCase().includes(nq));
    const uniq = name || `@${handle}`;
    if (storeHit && !seenStore.has(uniq)) {
      storeMatches.push({ type: 'store', id: handle, label: name || `@${handle}`, store: s });
      seenStore.add(uniq);
      if (storeMatches.length >= 6) break;
    }
  }

  // mix a little: first some products, then stores
  const mixed = [...prodMatches.slice(0, 4), ...storeMatches.slice(0, 4)];
  return mixed.slice(0, 8);
}, [q, listings, activeStores]);



  // Build applied filter chips (derived from parsed)
  const appliedChips = useMemo(() => {
    const chips = [];
    if (parsed.category) chips.push({ type: "category", label: parsed.category });
    if (parsed.department) chips.push({ type: "department", label: parsed.department });
    if (parsed.condition) chips.push({ type: "condition", label: parsed.condition });
    if (parsed.delivery) chips.push({ type: "delivery", label: parsed.delivery });
    if (Number.isInteger(parsed.maxDays)) chips.push({ type: "maxDays", label: `≤ ${parsed.maxDays} days` });
    return chips;
  }, [parsed]);

  // Remove a chip by reconstructing the query without its consumed keyword(s)
  function removeChip(chip) {
    let words = q.split(/\s+/).filter(Boolean);

    const tryRemove = (arr) => {
      for (const w of arr) {
        const i = words.findIndex((t) => t.toLowerCase() === w.toLowerCase());
        if (i !== -1) words.splice(i, 1);
      }
    };

    if (chip.type === "category") {
      const row = CATEGORY_SYNONYMS.find((r) => r.value === chip.label);
      if (row) tryRemove(row.words);
    } else if (chip.type === "department") {
      const row = DEPARTMENT_KEYWORDS.find((r) => r.value === chip.label);
      if (row) tryRemove(row.words);
    } else if (chip.type === "condition") {
      const row = CONDITION_KEYWORDS.find((r) => r.value === chip.label);
      if (row) tryRemove(row.words);
    } else if (chip.type === "delivery") {
      const row = DELIVERY_KEYWORDS.find((r) => r.value === chip.label);
      if (row) tryRemove(row.words);
    } else if (chip.type === "maxDays") {
      // remove any "7 days"/"3 day" like fragments
      words = words.filter((w) => !/^\d{1,3}$/.test(w) && !/^day(s)?$/i.test(w));
    }

    setQuery(words.join(" "));
    inputRef.current?.focus();
  }

  const onSubmit = (e) => {
    e.preventDefault();
    saveRecent(query);
    document.getElementById("ms-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const showEmpty =
    q &&
    ((tab === "Products" && productResults.length === 0) ||
      (tab === "Stores" && storeResults.length === 0));

  return (
    <div className="ms-container">
      {/* Top Bar */}
      <div className="ms-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="ms-back"
          onClick={() => navigate(-1)}
        />

        {/* Search box */}
        <form className="ms-searchbar" onSubmit={onSubmit}>
          <input
            ref={inputRef}
            type="search"
            inputMode="search"
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search Marketplace"
            autoFocus
            aria-label="Search Marketplace"
          />
          <img
            src={clearIcon}
            className={`ms-clear ${query ? "visible" : "hidden"}`}
            alt="Clear"
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
            title="Clear"
          />
        </form>
      </div>



      {/* Tabs */}
      <div className="ms-tabs">
        {["Products", "Stores"].map((t) => (
          <button
            key={t}
            className={`ms-tab ${t === tab ? "active" : ""}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
        <div
          className="ms-slider"
          style={{ left: tab === "Products" ? 0 : "50%" }}
        />
      </div>


{/* Inline Suggestions just under tabs */}
{q && quickSuggestions.length > 0 && (
  <div className="ms-section ms-suggest-inline">
    <div className="ms-section-head">
      <h4>Suggestions</h4>
    </div>

    <div className="ms-suggest-list">
      {quickSuggestions.map((sug, i) => (
        <button
          key={`${sug.type}-${sug.id}-${i}`}
          className="ms-suggest-chip"
          onClick={() => {
            if (sug.type === 'product') navigate(`/product/${sug.id}`);
            else navigate(`/store/${sug.id}`);
          }}
          title={sug.label}
        >
          <span className="ms-suggest-emoji" aria-hidden>
            {sug.type === 'product' ? '🛍️' : '🏪'}
          </span>
          <span
            className="ms-suggest-text"
            dangerouslySetInnerHTML={{ __html: highlightMatch(sug.label, q) }}
          />
        </button>
      ))}
    </div>
  </div>
)}



      {/* Recent (only) when no query */}
      {!q && recent.length > 0 && (
        <div className="ms-section">
          <div className="ms-section-head">
            <h4>Recent</h4>
            <button onClick={clearRecent} className="ms-clear-btn">
              Clear
            </button>
          </div>
          <div className="ms-chips">
            {recent.map((r) => (
              <button
                key={r}
                className="ms-chip"
                onClick={() => setQuery(r)}
                title={r}
              >
                {r.length > 40 ? `${r.slice(0, 40)}…` : r}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Applied Filters (chips) */}
      {q && appliedChips.length > 0 && (
        <div className="ms-section">
          <div className="ms-section-head">
            <h4>Filters</h4>
          </div>
          <div className="ms-chips">
            {appliedChips.map((chip, i) => (
              <button
                key={`${chip.type}-${i}`}
                className="ms-chip ms-chip-filter"
                onClick={() => removeChip(chip)}
                title={`Remove ${chip.label}`}
              >
                {chip.label} ✕
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      <div id="ms-results" className="ms-results">
        {tab === "Products" && q && (
          <>
            <div className="ms-count">
              {productResults.length} result{productResults.length !== 1 ? "s" : ""}
            </div>
            <div className="ms-grid">
              {productResults.map((p) => {
                const sale = saleViewFor(p, piToUsdRate);
                const displayPi = sale ? sale.discounted : basePi(p, piToUsdRate);

                return (
                  <div
                    key={p._id}
                    className="ms-card"
                    onClick={() => navigate(`/product/${p._id}`)}
                  >
                    <div className="ms-img-wrap">
                      {sale && typeof sale.percent === "number" && sale.percent > 0 && (
                        <span className="sale-pill left" aria-label={`-${sale.percent}% off`}>
                          -{sale.percent}%
                        </span>
                      )}
                      <img
                        className="ms-card-img"
                        src={p.media?.[0] || "https://via.placeholder.com/300"}
                        onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/300")}
                        alt={p.name}
                      />
                    </div>

                    <div className="ms-card-info">

<div
  className="ms-card-title"
  dangerouslySetInnerHTML={{ __html: highlightMatch(p.name, q) }}
></div>

                      <div className="ms-card-sub">
                        {Number.isFinite(displayPi) ? `${displayPi.toFixed(2)} Pi` : "—"}
                      </div>
                      {/* Optional: tiny meta row to show why it matched */}
                      <div className="ms-card-meta">
                        {p.category && <span className="ms-meta-pill">{p.category}</span>}
                        {p.department && <span className="ms-meta-pill">{p.department}</span>}
                        {p.condition && <span className="ms-meta-pill">{p.condition}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {tab === "Stores" && q && (
          <>
            <div className="ms-count">
              {storeResults.length} store{storeResults.length !== 1 ? "s" : ""}
            </div>
            <div className="ms-stores">
              {storeResults.map((s) => (
                <div
                  key={s.businessUsername}
                  className="ms-store-row"
                  onClick={() => navigate(`/store/${s.businessUsername}`)}
                >
                  <img
                    className="ms-store-logo"
                    src={s.businessLogo || "https://via.placeholder.com/120"}
                    alt={s.businessName}
                  />
                  <div className="ms-store-meta">

<div
  className="ms-store-name"
  dangerouslySetInnerHTML={{ __html: highlightMatch(s.businessName, q) }}
></div>
<div
  className="ms-store-handle"
  dangerouslySetInnerHTML={{ __html: '@' + highlightMatch(s.businessUsername, q) }}
></div>


                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {showEmpty && (
          <div className="ms-empty">
            <div className="ms-empty-emoji">🕵️‍♂️</div>
            <div>No matches. Try another keyword.</div>
          </div>
        )}
      </div>
    </div>
  );
}
